import { CICLO, UNIDADES } from "@caint/data";
import { EixoChip, Gauge, Meter, SealMedal } from "@caint/design-system";
import { useMemo, useState } from "react";
import type { AvaliacaoRemota } from "../../lib/api.ts";
import {
  type CicloRede,
  type EstadoCiclo,
  anoVigente,
  anosComCiclo,
  ciclosDaRede,
  estadoDoCiclo,
  metricasDaRede,
} from "../../lib/ciclos.ts";
import { engine } from "../../lib/engine.ts";
import { notaPreenchida } from "../../lib/supervisao.ts";
import type { ItemMerged } from "../../state/useAppState.ts";
import "./dashboard.css";

export interface InicioDesktopProps {
  items: ItemMerged[];
  qaci: number;
  ims: number;
  go: (route: string, arg?: string) => void;
  avaliacaoAtual?: AvaliacaoRemota;
  /** Todos os ciclos já abertos na rede — a lista do card é derivada daqui. */
  avaliacoes: readonly AvaliacaoRemota[];
  /** Escopo EFETIVO do ciclo em foco já confirmado — define a fase do stepper. */
  escopoConfirmado: boolean;
  /** Troca o ciclo ativo e abre o painel dele (clique numa linha da lista). */
  onAbrirCiclo: (avaliacaoId: string) => void;
}

type ChaveSelo = "ouro" | "prata" | "bronze" | "dev" | "pend";

/** Cor + algarismo romano por selo (réplica do SealPill do protótipo). */
const SELO_PILL: Record<ChaveSelo, { clr: string; lbl: string; roman: string }> = {
  ouro: { clr: "var(--seal-ouro)", lbl: "OURO", roman: "III" },
  prata: { clr: "var(--seal-prata)", lbl: "PRATA", roman: "II" },
  bronze: { clr: "var(--seal-bronze)", lbl: "BRONZE", roman: "I" },
  dev: { clr: "var(--seal-dev)", lbl: "EM DESENV.", roman: "0" },
  pend: { clr: "var(--slate-400)", lbl: "PENDENTE", roman: "—" },
};

function SealPill({ chave }: { chave: ChaveSelo }) {
  const { clr, lbl, roman } = SELO_PILL[chave] ?? SELO_PILL.pend;
  return (
    <span
      className="seal-pill"
      style={{
        background: `color-mix(in srgb, ${clr} 14%, white)`,
        color: `color-mix(in srgb, ${clr} 75%, black)`,
      }}
    >
      <span className="sd" style={{ background: clr }}>
        {roman}
      </span>
      {lbl}
    </span>
  );
}

function Stat({
  label,
  value,
  foot,
  color,
}: {
  label: string;
  value: React.ReactNode;
  foot?: string;
  color?: string;
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={color ? { color } : undefined}>
        {value}
      </div>
      {foot && <div className="kpi-foot">{foot}</div>}
    </div>
  );
}

/**
 * Estados do ciclo (stepper). As FASES vêm do seed (a metodologia, doc 16); a
 * fase CORRENTE é derivada do ciclo em foco por `estadoDoCiclo`. Antes vinha o
 * `CICLO.faseAtual` fixo do seed de demonstração — o stepper marcava
 * "Monitoramento" para todo mundo, inclusive para quem nem tinha confirmado o
 * escopo, e o período "Jun → Nov/2026" estava escrito à mão.
 */
function CycleStepper({ estado }: { estado: EstadoCiclo }) {
  return (
    <div className="card card-pad" style={{ marginBottom: "var(--space-5)" }}>
      <div className="between" style={{ marginBottom: "var(--space-4)" }}>
        <div className="panel-label">{estado.titulo} · estados do ciclo</div>
        <span className="spec">{estado.periodo || "sem janela definida"}</span>
      </div>
      <div className="stepper">
        {CICLO.fases.map((f, i) => {
          const cls = i < estado.fase ? "done" : i === estado.fase ? "active" : "todo";
          return (
            <div key={f} style={{ display: "contents" }}>
              {i > 0 && <div className="step-line" />}
              <div className={`step ${cls}`}>
                <div className="step-dot">{cls === "done" ? "✓" : i + 1}</div>
                <div className="step-name">{f}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Card "Unidades da rede": uma linha por CICLO que existe de fato (não pelo
 * cadastro estático de unidades), do mais novo para o mais antigo.
 *
 * Antes a lista vinha de `UNIDADES`: as 104 unidades do cadastro apareciam
 * sempre, inclusive as que nunca tiveram ciclo, com "—" de fase, 0% preenchido
 * e selo PENDENTE (os campos de demonstração saíram do pacote de dados).
 *
 * O ano é CONTROLADO pelo pai: o título da página e os KPIs do topo mostram o
 * mesmo recorte, então o filtro não pode morar aqui dentro.
 */
function CiclosDaRede({
  lista,
  anos,
  ano,
  onAno,
  avaliacaoAtual,
  onAbrirCiclo,
}: {
  lista: readonly CicloRede[];
  anos: readonly number[];
  /** `null` = todos os anos. */
  ano: number | null;
  onAno: (ano: number | null) => void;
  avaliacaoAtual?: AvaliacaoRemota;
  onAbrirCiclo: (avaliacaoId: string) => void;
}) {
  return (
    <div className="card">
      <div className="between" style={{ padding: "var(--space-5) var(--space-5) var(--space-4)" }}>
        <div className="panel-label">Unidades da rede · {ano == null ? "todos os anos" : `Ciclo ${ano}`}</div>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
          <span className="spec">
            {lista.length} ciclo{lista.length === 1 ? "" : "s"}
          </span>
          {anos.length > 0 && (
            <select
              className="m-select"
              aria-label="Ano dos ciclos exibidos"
              value={ano == null ? "todos" : String(ano)}
              onChange={(e) => onAno(e.target.value === "todos" ? null : Number(e.target.value))}
            >
              {anos.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
              <option value="todos">Todos os anos</option>
            </select>
          )}
        </div>
      </div>
      {lista.length === 0 ? (
        <div style={{ padding: "0 var(--space-5) var(--space-5)" }} className="page-sub">
          Nenhum ciclo aberto ainda. Os ciclos aparecem aqui assim que forem criados em Ciclos.
        </div>
      ) : (
        lista.map((c) => (
          <LinhaCiclo
            key={c.avaliacaoId}
            ciclo={c}
            ativo={c.avaliacaoId === avaliacaoAtual?.id}
            onAbrir={onAbrirCiclo}
          />
        ))
      )}
    </div>
  );
}

/** Uma linha do card — um ciclo. Os números já vêm resolvidos pelo pai. */
function LinhaCiclo({
  ciclo: c,
  ativo,
  onAbrir,
}: {
  ciclo: CicloRede;
  ativo: boolean;
  onAbrir: (avaliacaoId: string) => void;
}) {
  return (
    <button type="button" className="unit-row" onClick={() => onAbrir(c.avaliacaoId)}>
      <div>
        <div className="unit-name">
          {c.unidade}{" "}
          {ativo && (
            <span className="chip" style={{ marginLeft: 6, fontSize: "var(--text-2xs)" }}>
              foco
            </span>
          )}
        </div>
        <div className="unit-meta">
          {c.fase} · {c.periodo}
          {c.municipio !== "—" ? ` · ${c.municipio}` : ""}
          {c.cnes ? ` · CNES ${c.cnes}` : ""}
        </div>
      </div>
      <span className={`ciclo-badge ${c.aberto ? "on" : "off"}`} style={{ marginLeft: 0 }}>
        {c.aberto ? "Aberto" : "Encerrado"}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, width: 120 }}>
        <div className="mini-meter">
          <i style={{ width: `${c.preenchido}%` }} />
        </div>
        <span className="spec">
          {c.preenchido}% · {c.respondidos}/{c.itensEscopo} itens
        </span>
      </div>
      <b
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--text-sm)",
          width: 56,
          textAlign: "right",
        }}
      >
        {c.imgq == null ? "—" : engine.pct(c.imgq)}
      </b>
      <SealPill chave={(c.selo as ChaveSelo) ?? "pend"} />
    </button>
  );
}

export function InicioDesktop({
  items,
  qaci,
  ims,
  go,
  avaliacaoAtual,
  avaliacoes,
  escopoConfirmado,
  onAbrirCiclo,
}: InicioDesktopProps) {
  const ativa = UNIDADES.find((u) => u.id === avaliacaoAtual?.unidadeId);
  const calc = engine.imgq(items, "mi", qaci, ims);
  const selo = engine.selo(calc.ajustado);
  const coreAlertas = calc.core0 + calc.core1;
  // Preenchimento VIVO do ciclo em foco (inclui o que ainda não sincronizou).
  const respondidosAtivo = items.filter((it) => notaPreenchida(it.mi)).length;

  // Ciclos da rede com o ciclo em FOCO corrigido para o número vivo — o único
  // que pode estar à frente do cache do servidor. A lista e os KPIs derivam
  // daqui, então mostram sempre o mesmo número.
  const ciclos = useMemo(() => {
    const base = ciclosDaRede(avaliacoes);
    const id = avaliacaoAtual?.id;
    if (!id) {
      return base;
    }
    return base.map((c) =>
      c.avaliacaoId === id
        ? {
            ...c,
            // Sem nenhum item preenchido o IMGQ vivo seria 0 — mas o ciclo não
            // vale zero, ele ainda não tem base. Mantém o cache/"—".
            imgq: respondidosAtivo > 0 ? calc.ajustado : c.imgq,
            selo: respondidosAtivo > 0 ? selo.chave : c.selo,
            respondidos: respondidosAtivo,
            itensEscopo: items.length,
            preenchido: items.length > 0 ? Math.round((respondidosAtivo / items.length) * 100) : 0,
          }
        : c,
    );
  }, [avaliacoes, avaliacaoAtual?.id, calc.ajustado, selo.chave, respondidosAtivo, items.length]);

  const anos = useMemo(() => anosComCiclo(ciclos), [ciclos]);
  // `undefined` = ninguém escolheu ainda → segue o ano vigente enquanto os
  // ciclos carregam. `null` = a pessoa escolheu "Todos os anos".
  const [escolha, setEscolha] = useState<number | null | undefined>(undefined);
  const ano = escolha === undefined ? anoVigente(anos) : escolha;
  const doAno = useMemo(() => (ano == null ? ciclos : ciclos.filter((c) => c.ano === ano)), [ciclos, ano]);
  const rede = useMemo(() => metricasDaRede(doAno), [doAno]);
  // O ciclo em foco dentro da lista — dá a FASE e o período reais da visita
  // (o cadastro de unidades não tem mais esses campos de demonstração).
  const emFoco = ciclos.find((c) => c.avaliacaoId === avaliacaoAtual?.id);
  const estado = useMemo(
    () => estadoDoCiclo(avaliacoes, avaliacaoAtual?.id ?? null, escopoConfirmado),
    [avaliacoes, avaliacaoAtual?.id, escopoConfirmado],
  );
  const recorte = ano == null ? "todos os anos" : `Ciclo ${ano}`;

  const eixos: [string, number | null][] = [
    ["IMGO", calc.imgo],
    ["IMQA", calc.imqa],
    ["IMGR", calc.imgr],
  ];
  // O CNES já vem no chip ao lado — aqui fica o que ele não diz.
  const metaLine = [ativa?.municipio, estado.periodo || null].filter(Boolean).join(" · ");

  return (
    <div className="dk-page dk-dash">
      <div className="page-head between">
        <div>
          <div className="page-eyebrow">Painel de controle · CAINT</div>
          <h1 className="page-title">Visão do {recorte}</h1>
          <p className="page-sub">
            {rede.unidades} unidade{rede.unidades === 1 ? "" : "s"} com ciclo · {rede.ciclos} ciclo
            {rede.ciclos === 1 ? "" : "s"} · {rede.abertos} na janela de preenchimento
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => go("checklist")}>
          Abrir checklist da unidade
        </button>
      </div>

      <CycleStepper estado={estado} />

      <div className="grid-auto g4" style={{ marginBottom: "var(--space-5)" }}>
        <Stat
          label="Unidades em avaliação"
          value={rede.unidades}
          foot={`${rede.comBase} com IMGQ calculado`}
        />
        <Stat
          label="IMGQ médio (rede)"
          value={rede.mediaImgq == null ? "—" : engine.pct(rede.mediaImgq)}
          foot="meta institucional 46%"
          color="var(--primary)"
        />
        <Stat
          label="Selos previstos"
          value={rede.selosPrevistos}
          foot={`${rede.selos.dev} em desenvolvimento · ${rede.selos.pend} sem base`}
        />
        <Stat
          label="Alertas CORE (sua unidade)"
          value={coreAlertas}
          foot={calc.travado ? "selo travado" : "sem trava"}
          color={coreAlertas > 0 ? "var(--danger)" : "var(--success)"}
        />
      </div>

      {/* unidade em foco */}
      <div className="card" style={{ marginBottom: "var(--space-5)", overflow: "hidden" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            gap: "var(--space-6)",
            alignItems: "center",
            padding: "var(--space-6)",
          }}
        >
          <Gauge value={calc.ajustado} label="IMGQ Ajustado" size={132} />
          <div style={{ minWidth: 0 }}>
            <div className="row-wrap" style={{ marginBottom: 8 }}>
              {ativa?.cnes && <span className="chip chip-code">{ativa.cnes}</span>}
              <span className="badge" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                <span className="dot" />
                Sua unidade · {emFoco ? `${emFoco.fase} · ${emFoco.periodo}` : "sem ciclo"}
              </span>
            </div>
            <div style={{ fontSize: "var(--text-xl)", fontWeight: 800, letterSpacing: "-0.02em" }}>
              {ativa?.nome ?? "Unidade"}
            </div>
            <div className="page-sub" style={{ marginTop: 2 }}>
              {metaLine}
            </div>
            <div className="row-wrap" style={{ marginTop: "var(--space-4)" }}>
              {eixos.map(([k, v]) => (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <EixoChip eixo={k as "IMGO" | "IMQA" | "IMGR"} />
                  <b
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--text-sm)",
                      color: v != null && v < 0.46 ? "var(--danger)" : "var(--text)",
                    }}
                  >
                    {engine.num2(v)}
                  </b>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <SealMedal chave={selo.chave} roman={selo.romano} size={76} />
            <div style={{ textAlign: "center" }}>
              <div className="seal-label">{selo.rotulo}</div>
              <div className="seal-range">{selo.nivel}</div>
            </div>
            <div className="row-wrap">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => go("relatorio")}>
                Painel
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => go("checklist")}>
                Checklist
              </button>
            </div>
          </div>
        </div>
        <div style={{ padding: "0 var(--space-6) var(--space-6)" }}>
          <Meter value={calc.ajustado} />
        </div>
        {calc.travado && (
          <div style={{ padding: "0 var(--space-6) var(--space-6)" }}>
            <div
              style={{
                display: "flex",
                gap: "var(--space-3)",
                padding: "var(--space-4)",
                borderRadius: "var(--radius-md)",
                border: "1px solid",
                borderLeftWidth: 4,
                background: "var(--danger-soft)",
                borderColor: "color-mix(in srgb, var(--danger) 35%, white)",
                color: "color-mix(in srgb, var(--danger) 80%, black)",
              }}
            >
              <span style={{ flex: "0 0 auto", marginTop: 1 }}>⛔</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ fontWeight: 700, fontSize: "var(--text-sm)" }}>
                  {coreAlertas > 0
                    ? `${coreAlertas} item(ns) CORE em maturidade 0/1`
                    : "Subíndice abaixo da meta (0,46)"}{" "}
                  — selo travado em ≤ 59,9%
                </div>
                <div style={{ fontSize: "var(--text-sm)", color: "var(--slate-600)" }}>
                  Regra de exclusão: requisito essencial ausente/em estado inicial ou eixo abaixo da meta
                  impede Prata/Ouro.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ciclos da rede — os que EXISTEM, do mais novo para o mais antigo */}
      <CiclosDaRede
        lista={doAno}
        anos={anos}
        ano={ano}
        onAno={setEscolha}
        avaliacaoAtual={avaliacaoAtual}
        onAbrirCiclo={onAbrirCiclo}
      />
    </div>
  );
}
